import { Button } from 'components/Button';
import { SplitButton } from 'components/SplitButton';
import ButtonLoader from 'components/loading/ButtonLoader';
import { QuickQuoteConfig } from 'contexts/serviceContext';
import { DrumkitPlatform } from 'contexts/sidebarStateContext';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  supportsMailClient,
  supportsMailClientSend,
} from 'lib/mailclient/interface';
import { Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import QuickQuoteReplyActionMode from 'types/enums/QuickQuoteReplyActionMode';
import { cn } from 'utils/shadcn';

import { getQuickQuoteReplyActionMode } from './getQuickQuoteReplyActionMode';

// Stacked QQ actions: shared height and typography.
const quoteReplyActionSegmentClassName =
  'h-8 min-h-8 text-sm leading-none py-0';
const quoteReplyActionButtonClassName = `${quoteReplyActionSegmentClassName} px-3`;

type OutlookQuoteReplyActionsProps = {
  drumkitPlatform: DrumkitPlatform;
  isSharedMailbox: boolean;
  isOutlookReply: boolean;
  loadingDraftReply: boolean;
  // Extra disable (e.g. empty To); loading still shows the button spinner.
  disabled?: boolean;
  onSend: () => void;
  onDraft: () => void;
  onCopy: () => void;
  logProperties?: Record<string, unknown>;
  draftButtonNamePosthog?: ButtonNamePosthog;
  addToCurrentDraftButtonNamePosthog?: ButtonNamePosthog;
  copyButtonNamePosthog?: ButtonNamePosthog;
};

export function OutlookQuoteReplyActions({
  drumkitPlatform,
  isSharedMailbox,
  isOutlookReply,
  loadingDraftReply,
  disabled = false,
  onSend,
  onDraft,
  onCopy,
  logProperties,
  draftButtonNamePosthog = ButtonNamePosthog.CreateDraftReply,
  addToCurrentDraftButtonNamePosthog = ButtonNamePosthog.AddReplyToCurrentDraft,
  copyButtonNamePosthog = ButtonNamePosthog.CopyQuoteToClipboard,
}: OutlookQuoteReplyActionsProps) {
  const { quickQuoteConfig } = useServiceFeatures();
  const isTql = useIsTqlInbox();
  const replyActionMode = resolveReplyActionMode(
    drumkitPlatform,
    isTql,
    quickQuoteConfig
  );
  const showSendReply =
    replyActionMode === QuickQuoteReplyActionMode.SendPrimary ||
    replyActionMode === QuickQuoteReplyActionMode.SendOnly;
  const showDraftReplyButton =
    replyActionMode === QuickQuoteReplyActionMode.DraftPrimary ||
    replyActionMode === QuickQuoteReplyActionMode.SendPrimary;
  const useSendDraftSplitButton =
    replyActionMode === QuickQuoteReplyActionMode.SendPrimary;
  const actionsDisabled = loadingDraftReply || disabled;

  if (!supportsMailClient(drumkitPlatform)) {
    return null;
  }

  if (isSharedMailbox) {
    return (
      <Button
        size='auto'
        className={cn('w-full mt-4', quoteReplyActionButtonClassName)}
        type='button'
        buttonNamePosthog={copyButtonNamePosthog}
        logProperties={logProperties}
        onClick={onCopy}
      >
        {ButtonText.CopyToClipboard}
      </Button>
    );
  }

  if (isOutlookReply && drumkitPlatform === DrumkitPlatform.Outlook) {
    return (
      <Button
        size='auto'
        className={cn('w-full mt-4', quoteReplyActionButtonClassName)}
        type='button'
        buttonNamePosthog={addToCurrentDraftButtonNamePosthog}
        logProperties={logProperties}
        disabled={actionsDisabled}
        onClick={onDraft}
      >
        {loadingDraftReply ? (
          <ButtonLoader />
        ) : (
          ButtonText.AddReplyToCurrentDraft
        )}
      </Button>
    );
  }

  return (
    <>
      {useSendDraftSplitButton && (
        <SplitButton
          className='mt-4'
          size='auto'
          segmentClassName={quoteReplyActionSegmentClassName}
          primaryClassName='px-3'
          primaryLabel={loadingDraftReply ? <ButtonLoader /> : ButtonText.Send}
          primaryButtonNamePosthog={ButtonNamePosthog.SendReply}
          onPrimaryClick={onSend}
          disabled={actionsDisabled}
          logProperties={logProperties}
          menuTriggerAriaLabel='More quote reply options'
          menuItems={[
            {
              id: 'draft-email',
              label: ButtonText.DraftEmail,
              buttonNamePosthog: draftButtonNamePosthog,
              onSelect: onDraft,
            },
          ]}
        />
      )}

      {showSendReply && !useSendDraftSplitButton && (
        <Button
          size='auto'
          className={cn('w-full mt-4', quoteReplyActionButtonClassName)}
          type='button'
          buttonNamePosthog={ButtonNamePosthog.SendReply}
          logProperties={logProperties}
          disabled={actionsDisabled}
          onClick={onSend}
        >
          {loadingDraftReply ? <ButtonLoader /> : ButtonText.Send}
        </Button>
      )}

      {showDraftReplyButton && !useSendDraftSplitButton && (
        <Button
          size='auto'
          className={cn('w-full mt-4', quoteReplyActionButtonClassName)}
          type='button'
          buttonNamePosthog={draftButtonNamePosthog}
          logProperties={logProperties}
          disabled={actionsDisabled}
          onClick={onDraft}
        >
          {loadingDraftReply ? <ButtonLoader /> : ButtonText.DraftEmail}
        </Button>
      )}
    </>
  );
}

function resolveReplyActionMode(
  drumkitPlatform: DrumkitPlatform,
  isTql: boolean,
  quickQuoteConfig: Undef<QuickQuoteConfig>
): QuickQuoteReplyActionMode {
  if (!supportsMailClientSend(drumkitPlatform)) {
    return QuickQuoteReplyActionMode.DraftPrimary;
  }

  if (isTql) {
    return QuickQuoteReplyActionMode.SendPrimary;
  }

  return getQuickQuoteReplyActionMode(quickQuoteConfig ?? null);
}
